import React, { useState } from "react";
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Button,
  Textarea,
  Tooltip,
  makeStyles,
  Text,
  Field,
  Spinner,
  Toast,
  ToastTitle,
} from "@fluentui/react-components";
import { Connector16Regular } from "@fluentui/react-icons";
import AdminService from "../AdminService";

const useStyles = makeStyles({
  dialogSurface: {
    width: "600px",
    maxWidth: "90vw",
  },

  content: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    marginTop: "8px",
  },

  input: {
    width: "100%",
  },

  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    marginTop: "16px",
  },

  helperText: {
    opacity: 0.7,
  },
  // Tooltip styles
  tooltipContent: {
    maxWidth: "420px",
    wordBreak: "break-all",
    whiteSpace: "pre-wrap",
    fontSize: "12px",
  },
});

export default function EditAcsDialog({ row, onSuccess, dispatchToast }) {
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(row?.acsConnectionString || "");
  const [loading, setLoading] = useState(false);

  const handleOpen = () => {
    setValue(row?.acsConnectionString || "");
    setOpen(true);
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      await AdminService.editACSConnection({
        "tenantId": row?.tenantId,
        "config": {
          "acs": {
            "connectionString": value
          }
        }
      });

      dispatchToast(
        <Toast>
          <ToastTitle>
            ACS Connection updated successfully for {row?.tenantName}
          </ToastTitle>
        </Toast>,
        { intent: "success" }
      );

      setOpen(false);
      onSuccess?.();
    } catch (err) {
      console.error("Failed to update ACS:", err);

      dispatchToast(
        <Toast>
          <ToastTitle>
            {err?.message || "Failed to update ACS connection"}
          </ToastTitle>
        </Toast>,
        { intent: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Tooltip
        relationship="label"
        content={
          <div className={styles.tooltipContent}>
            <Text weight="semibold">ACS Connection</Text>
            <br />
            {row?.acsConnectionString || "Not set"}
          </div>
        }
      >
        <Button
          size="small"
          appearance="transparent"
          icon={<Connector16Regular />}
          onClick={handleOpen}
        />
      </Tooltip>

      {/* Dialog */}
      <Dialog open={open} onOpenChange={(_, data) => setOpen(data.open)}>
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogTitle>Edit ACS Connection String</DialogTitle>

            <DialogContent className={styles.content}>
              <Text className={styles.helperText}>
                {`Update the Azure connection string for ${row?.tenantName} tenant.`}
              </Text>

              <Field label="ACS Connection String" required>
                <Textarea
                  className={styles.input}
                  value={value}
                  rows={4}
                  onChange={(e, data) => setValue(data.value)}
                  placeholder="Enter ACS connection string"
                />
              </Field>
            </DialogContent>

            <DialogActions className={styles.footer}>
              <Button
                appearance="secondary"
                onClick={() => setOpen(false)}
                disabled={loading}
              >
                Cancel
              </Button>

              <Button
                appearance="primary"
                onClick={handleSave}
                disabled={loading || !value}
                icon={loading ? <Spinner size="tiny" /> : null}
              >
                {loading ? "Saving..." : "Save Changes"}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
}